/* eslint-disable @typescript-eslint/ban-ts-comment */
/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useMemo, useState } from "react";
import ReactECharts from "echarts-for-react";
import CardLayout from "../Cards/CardLayout";
import { COLORS, REFETCH_CONFIGURATION } from "../../utils/constants";
import { useFetchData } from "../../customHooks/useGetDashboardData";
import Loader from "../feedBacks/loader";
import CardError from "../feedBacks/CardError";
import NoContent from "../feedBacks/NoContent";
import { EChartsOption } from "echarts";
import { useAppSelector } from "../../store/hooks";
import { createToolboxConfig } from "../../utils/utils";
import { IGeneralChartProps } from "../../utils/interfaces";
import { useUtils } from "../../customHooks/useUtils";

interface IVoltageProfileChart {
  dataset: {
    times: string[];
    red_phase: number[];
    yellow_phase: number[];
    blue_phase: number[];
  };
}

const VoltageProfileChart = ({
  queryKey,
  route,
  params,
  title,
}: IGeneralChartProps) => {
  const { FEEDBACK_MESSAGES, t } = useUtils();
  const [isLocalLoading, setIsLocalLoading] = useState(false);
  const startDate = useAppSelector((state) => state.filter.startDate);
  const endDate = useAppSelector((state) => state.filter.endDate);
  const {
    data,
    isLoading: isDataLoading,
    refetch,
    error,
  } = useFetchData(
    queryKey,
    route,
    {
      ...params,
      start_date: startDate,
      end_date: endDate,
    },
    // @ts-ignore
    {
      ...REFETCH_CONFIGURATION,
    }
  );

  // Get data from API response or set defaults if not available
  const voltageData = useMemo(() => {
    return data
      ? (data as IVoltageProfileChart).dataset
      : {
          times: [],
          red_phase: [],
          yellow_phase: [],
          blue_phase: [],
        };
  }, [data]);

  // Determine if the data is daily format (YYYY-MM-DD) or has time components
  const isDateOnly = useMemo(() => {
    if (voltageData.times.length === 0) return false;
    const firstTimestamp = voltageData.times[0];
    // Check if the timestamp is in YYYY-MM-DD format (no time component)
    return /^\d{4}-\d{2}-\d{2}$/.test(firstTimestamp);
  }, [voltageData]);

  // Format dates for better display
  const formattedTimes = useMemo(() => {
    return voltageData.times.map((timestamp) => {
      const date = new Date(timestamp);

      // Use different formatting based on whether it's date-only or has time
      let formattedDate;
      if (isDateOnly) {
        // For date-only data, format as "MMM DD" (e.g., "Mar 12")
        formattedDate = `${date.toLocaleString("default", {
          month: "short",
        })} ${date.getDate()}`;
      } else {
        // For data with time, format as "MMM DD HH:mm" (e.g., "Mar 12 14:30")
        formattedDate = `${date.toLocaleString("default", {
          month: "short",
        })} ${date.getDate()} ${String(date.getHours()).padStart(
          2,
          "0"
        )}:${String(date.getMinutes()).padStart(2, "0")}`;
      }
      return formattedDate;
    });
  }, [voltageData, isDateOnly]);

  // Determine the time difference between data points
  const timeGranularity = useMemo(() => {
    if (voltageData.times.length < 2) return "unknown";

    // For date-only data, assume it's daily
    if (isDateOnly) return "days";

    const date1 = new Date(voltageData.times[0]);
    const date2 = new Date(voltageData.times[1]);

    const diffMinutes = Math.abs(
      (date2.getTime() - date1.getTime()) / (1000 * 60)
    );

    if (diffMinutes < 60) return "minutes";
    if (diffMinutes < 24 * 60) return "hours";
    return "days";
  }, [voltageData, isDateOnly]);

  // Set interval for x-axis labels based on time granularity and data length
  const determineInterval = useMemo(() => {
    const dataLength = formattedTimes.length;

    switch (timeGranularity) {
      case "minutes":
        // For minute data, show labels every 15 minutes
        return (index: number, value: string) => {
          if (!value) return false;
          const parts = value.split(" ");
          if (parts.length < 3) return false;
          const timeParts = parts[2].split(":");
          if (timeParts.length < 2) return false;
          const minutes = parseInt(timeParts[1], 10);
          return minutes % 30 === 0;
        };

      case "hours":
        // For hourly data, show labels every hour
        return (index: number, value: string) => {
          if (!value) return false;
          const parts = value.split(" ");
          if (parts.length < 3) return false;
          const timeParts = parts[2].split(":");
          if (timeParts.length < 2) return false;
          const minutes = parseInt(timeParts[1], 10);
          return minutes === 0;
        };

      case "days":
        // For daily data, use numeric interval based on data length
        if (dataLength <= 7) return 0; // Show all labels for a week or less
        if (dataLength <= 31) return 1; // Show every other day for a month
        if (dataLength <= 90) return 2; // Show every third day for quarter
        return Math.floor(dataLength / 30); // Show about 30 labels for longer periods

      default:
        // Default fallback
        return Math.max(Math.floor(dataLength / 30), 0);
    }
  }, [formattedTimes.length, timeGranularity]);

  const yAxisConfig = useMemo(() => {
    // Get max value from all three phases
    const allValues = [
      ...voltageData.red_phase,
      ...voltageData.yellow_phase,
      ...voltageData.blue_phase,
    ];

    const maxValue = Math.max(
      ...allValues.filter((val) => !isNaN(val) && val !== null)
    );
    const minValue = Math.min(
      ...allValues.filter((val) => !isNaN(val) && val !== null)
    );
    // Default configuration
    const config = {
      min: 200,
      max: undefined as number | undefined,
      interval: undefined as number | undefined,
    };

    // Set configuration based on max value
    if (maxValue <= 500) {
      config.min = 200;
      config.max = 300;
      config.interval = 20;
    } else if (maxValue > 500) {
      config.min = Math.ceil(minValue - 50);
      config.max = Math.ceil(maxValue + 50);
      config.interval = Math.ceil((config.max - config.min) / 5); // Divide into 5 intervals
      // No max - will be automatically determined
    }

    return config;
  }, [voltageData]);

  const options: EChartsOption = useMemo(() => {
    return {
      ...createToolboxConfig({
        chartName: t("chart.voltage_profile"),
        top: "-5px",
        right: "5px",
      }),
      tooltip: {
        trigger: "axis",
        formatter: function (params: any) {
          let result = params[0].name + "<br/>";
          params.forEach((param: any) => {
            // Display the phase name and value with 2 decimal places
            result +=
              param.seriesName + ": " + param.value.toFixed(2) + " V<br/>";
          });
          return result;
        },
      },
      grid: {
        top: "15%",
        left: "3%",
        right: "3%",
        bottom: "20%",
        containLabel: true,
      },
      dataZoom: [
        {
          type: "slider",
          show: true,
          start: 0,
          end: 100,
          height: 30,
          bottom: 4,
          borderColor: "#ccc",
          fillerColor: "rgba(124, 135, 142, 0.2)",
          handleStyle: {
            color: "#7C878E",
            borderColor: "#7C878E",
          },
        },
        {
          type: "inside",
          start: 0,
          end: 100,
        },
      ],

      xAxis: {
        type: "category",
        name: t("chart.time_label"),
        nameLocation: "middle",
        nameGap: 60,
        boundaryGap: false,
        data: formattedTimes, // Use the formatted times array for x-axis
        axisLabel: {
          rotate: 45,
          interval: determineInterval,
          margin: 15,
          formatter: (value: string) => value,
          width: 80,
          align: "right",
          overflow: "truncate",
          showMaxLabel: true,
          color: "#333",
          fontSize: 8,
        },
        axisTick: {
          alignWithLabel: true,
          interval: 0,
        },
      },
      yAxis: {
        type: "value",
        name: t("chart.voltage_profile_label"),
        nameLocation: "middle",
        nameGap: 40,
        min: yAxisConfig.min,
        max: yAxisConfig.max,
        // interval: yAxisConfig.interval,

        axisLine: {
          show: true,
          lineStyle: {
            color: "#000",
            width: 0.8,
          },
        },
        axisTick: {
          show: false,
        },
        splitLine: {
          lineStyle: {
            type: "dashed",
            color: "#eee",
          },
        },
      },
      series: [
        {
          name: t("chart.phase_a"),
          type: "line",
          data: voltageData.red_phase,
          smooth: true,
          lineStyle: {
            color: COLORS.PHASE_A,
          },
          symbol: "none",
        },
        {
          name: t("chart.phase_b"),
          type: "line",
          data: voltageData.yellow_phase,
          smooth: true,
          lineStyle: {
            color: COLORS.PHASE_B,
          },
          symbol: "none",
        },
        {
          name: t("chart.phase_c"),
          type: "line",
          data: voltageData.blue_phase,
          smooth: true,
          lineStyle: {
            color: COLORS.PHASE_C,
          },
          symbol: "none",
        },
      ],
    };
  }, [voltageData, formattedTimes, determineInterval, yAxisConfig, t]);

  const legend = [
    {
      text: t("chart.phase_a"),
      color: COLORS.PHASE_A,
    },
    {
      text: t("chart.phase_b"),
      color: COLORS.PHASE_B,
    },
    {
      text: t("chart.phase_c"),
      color: COLORS.PHASE_C,
    },
  ];

  const isLoading =
    // Show loading for initial page load
    isDataLoading ||
    // Show loading when filter is applied and when actually refetching
    isLocalLoading;

  useEffect(() => {
    if (startDate && endDate) {
      setIsLocalLoading(true);

      refetch().finally(() => {
        setIsLocalLoading(false);
      });
    }
  }, [startDate, endDate, refetch]);

  return (
    <CardLayout
      title={title || t("chart.voltage_profile")}
      className="lg:min-w-[570px] flex-1 !min-h-[450px]"
      besideTitleComponent={
        !isLoading && !error && voltageData.times.length > 0 ? (
          <div className="flex gap-2 justify-between">
            <div className="flex-1 flex gap-2 justify-end">
              {legend.map((item, idx) => (
                <div
                  key={idx}
                  className="flex items-center gap-1 rounded-full bg-primary-blackLighter2 px-3 py-2"
                >
                  <span
                    className="w-4 h-4 rounded-md"
                    style={{ backgroundColor: item.color }}
                  ></span>
                  <span className="text-[11px]">{item.text}</span>
                </div>
              ))}
            </div>
          </div>
        ) : null
      }
      besideComponentStyle="!flex-1 !flex justify-end"
    >
      <div className="flex flex-col h-full w-full !p-0 ">
        {isLoading ? (
          <div className=" flex justify-center items-center h-full w-full">
            <Loader />
          </div>
        ) : error ? (
          <CardError message={FEEDBACK_MESSAGES.ERROR} className="!h-[100px]" />
        ) : voltageData.times.length === 0 ? (
          <NoContent message={FEEDBACK_MESSAGES.NO_DATA} />
        ) : (
          <div className="h-full w-full">
            <ReactECharts
              option={options}
              className="!overflow-y-visible"
              style={{ height: "100%", overflowY: "visible" }}
            />
          </div>
        )}
      </div>
    </CardLayout>
  );
};

export default VoltageProfileChart;
